import {proxy, ref} from "valtio";

import {IQueryParamProvider} from "@/shared/contracts";
import {EventEmitter} from "@/shared/event-emitter";
import {DateHelper} from "@/shared/lib";
import {DateRangePicker, type DateRangePickerInstance} from "@/shared/models/form/datepickers";
import {RadioButton, type RadioButtonInstance, type RadioOption} from "@/shared/models/form/fields";
import {Select, type SelectInstance} from "@/shared/models/form/select";
import type {SelectOption} from "@/shared/models/form/select/types";

interface DateFlexibilityEvents {
    change: () => void;
    clear: () => void;
}

// Night options (1-14 nights) with translation keys
const NIGHT_OPTIONS: Array<SelectOption<number>> = Array.from({length: 14}, (_, i) => ({
    value: i + 1,
    label: `${i + 1} night${i > 0 ? "s" : ""}`, // fallback
    labelKey: "search.nightCount",
    labelParams: {count: i + 1},
}));

// Flexibility options with translation keys
const FLEXIBILITY_OPTIONS: Array<RadioOption<boolean>> = [
    {value: false, label: "Exact", labelKey: "search.exact"},
    {value: true, label: "Flexible", labelKey: "search.flexible"},
];
const EVENTS = Symbol("EVENTS");
class DateFlexibilityClass implements IQueryParamProvider {
    dateRange: DateRangePickerInstance;
    nightsSelect: SelectInstance<number, SelectOption<number>, false>;
    flexibilityRadio: RadioButtonInstance<boolean>;
    readonly events: EventEmitter<DateFlexibilityEvents>;

    // Committed values (shown in trigger and used for query params)
    committedStartDate: Date | null = null;
    committedEndDate: Date | null = null;
    committedNights: number | null = null;
    committedIsFlexible: boolean = false;

    // UI state
    isOpen: boolean = false;

    // Validation errors
    dateError: string | null = null;
    nightsError: string | null = null;

    // Flag to prevent recursion when clearing
    private isClearing: boolean = false;

    constructor() {
        this.dateRange = DateRangePicker({
            min: new Date(),
        });

        this.nightsSelect = Select<number, SelectOption<number>>({
            multiple: false,
            options: NIGHT_OPTIONS,
            isClearable: true,
            isSearchable: false,
        });

        this.flexibilityRadio = RadioButton<boolean>({
            options: FLEXIBILITY_OPTIONS,
            initialValue: false,
        });

        this.events = ref(new EventEmitter<DateFlexibilityEvents>()) as EventEmitter<DateFlexibilityEvents>;
    }

    [EVENTS](): void {
        // When calendar is cleared, clear all filters in this block
        this.dateRange.events.on("clear", () => {
            if (!this.isClearing) {
                this.clear();
            }
        });

        // Clear date error when user completes date selection
        this.dateRange.events.on("endSelect", () => {
            this.dateError = null;
            this.updateAvailableNights();
        });

        // Clear nights error when user selects nights
        this.nightsSelect.events.on("change", () => {
            this.nightsError = null;
        });
    }

    private updateAvailableNights(): void {
        const {startDate, endDate} = this.dateRange;
        if (!startDate || !endDate) return;

        const rangeDays = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        this.nightsSelect.options = NIGHT_OPTIONS.filter((opt) => opt.value <= rangeDays);

        // Auto-clear if current selection exceeds new range
        if (this.nightsSelect.value !== null && this.nightsSelect.value > rangeDays) {
            this.nightsSelect.clear();
        }
    }

    // Open dropdown
    open(): void {
        this.isOpen = true;
    }

    // Close dropdown
    close(): void {
        this.isOpen = false;
    }

    // Toggle dropdown
    toggle(): void {
        this.isOpen = !this.isOpen;
    }

    // Validate flexible mode requirements
    private validate(): boolean {
        this.clearErrors();

        const isFlexible = this.flexibilityRadio.value ?? false;

        if (isFlexible) {
            // In flexible mode, date range and nights are required
            if (!this.dateRange.startDate || !this.dateRange.endDate) {
                this.dateError = "Please select check-in and check-out dates";
            }
            if (!this.nightsSelect.value) {
                this.nightsError = "Please select number of nights";
            }
        }

        return !this.dateError && !this.nightsError;
    }

    // Clear validation errors
    clearErrors(): void {
        this.dateError = null;
        this.nightsError = null;
    }

    // Apply changes (called when user clicks Update)
    applyChanges(): void {
        // Validate before applying
        if (!this.validate()) {
            return;
        }

        // Commit draft values
        this.committedStartDate = this.dateRange.startDate;
        this.committedEndDate = this.dateRange.endDate;
        this.committedNights = this.nightsSelect.value;
        this.committedIsFlexible = this.flexibilityRadio.value ?? false;

        this.close();
        this.events.emit("change");
    }

    // Check if date flexibility has value
    get hasValue(): boolean {
        return this.committedStartDate !== null || this.committedEndDate !== null;
    }

    // Get query params (use committed values)
    getQueryParam(): string {
        const params: Array<string> = [];

        // Add date range params from committed values (checkIn/checkOut format MM-DD-YYYY)
        if (this.committedStartDate) {
            params.push(`checkIn=${DateHelper.formatDateForUrl(this.committedStartDate)}`);
        }
        if (this.committedEndDate) {
            params.push(`checkOut=${DateHelper.formatDateForUrl(this.committedEndDate)}`);
        }

        // Add flexible param (always add to show mode)
        params.push(`flexible=${this.committedIsFlexible}`);

        // Add nights param if flexible mode
        if (this.committedIsFlexible && this.committedNights) {
            params.push(`nights=${this.committedNights}`);
        }

        return params.join("&");
    }

    // Set from query params
    setFromQueryParams(params: URLSearchParams): void {
        // Set date range (draft) - using checkIn/checkOut param names
        // allowPast: true — dates from URL must be shown as-is even if in the past
        this.dateRange.setFromQueryParams(params, {start: "checkIn", end: "checkOut"}, {allowPast: true});

        // Set nights (draft) — clear if not present in URL
        const nightsValue = params.get("nights");
        if (nightsValue) {
            const nightsNumber = parseInt(nightsValue, 10);
            if (!isNaN(nightsNumber)) {
                this.nightsSelect.setSelectedByValue(nightsNumber);
            }
        } else {
            this.nightsSelect.clear();
        }

        // Set flexible flag (draft) — false if not present in URL
        this.flexibilityRadio.setValue(params.get("flexible") === "true");

        // Also set committed values (these are already applied from URL)
        this.committedStartDate = this.dateRange.startDate;
        this.committedEndDate = this.dateRange.endDate;
        this.committedNights = this.nightsSelect.value;
        this.committedIsFlexible = this.flexibilityRadio.value ?? false;
    }

    // Clear all selections
    clear(): void {
        this.isClearing = true;

        // Clear draft values
        this.dateRange.clear();
        this.nightsSelect.clear();
        this.flexibilityRadio.setValue(false);

        // Clear committed values
        this.committedStartDate = null;
        this.committedEndDate = null;
        this.committedNights = null;
        this.committedIsFlexible = false;

        // Clear errors
        this.clearErrors();

        this.close();
        this.isClearing = false;

        this.events.emit("clear");
    }
}

export type DateFlexibilityInstance = DateFlexibilityClass;

export function DateFlexibility(): DateFlexibilityInstance {
    const instance = proxy(new DateFlexibilityClass());
    instance[EVENTS]();
    return instance;
}
